import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { OmsActionType } from "./actions";

/**
 * Parcel writes key on `parcel.name`, so deleting and re-adding a parcel mints
 * a new `_id` while automation steps keep holding the old one.
 */

export interface ParcelReference {
  stepId: string;
  /** Author-facing step name, when the step carries one. */
  stepName?: string;
  stepType: StepType.OMS_ACTION | StepType.OMS_CODE;
  parcelId: string;
  /** Snapshot name embedded on the step. Action steps only. */
  parcelName?: string;
}

/** Steps carry more fields than this; only these are needed to find references. */
export interface ScannableStep {
  id: string;
  type: string;
  name?: string | undefined;
  actionType?: string | undefined;
  artifactId?: string | undefined;
  parcel?: { _id?: string | null; name?: string } | null | undefined;
  parcels?:
    | ({ _id?: string | null; name?: string } | null)[]
    | null
    | undefined;
}

const OBJECT_ID_LITERAL = /\b[0-9a-fA-F]{24}\b/g;

function isSetParcelStep(step: ScannableStep): boolean {
  return (
    step.type === StepType.OMS_ACTION &&
    step.actionType === OmsActionType.SET_PARCEL
  );
}

function actionStepReferences(step: ScannableStep): ParcelReference[] {
  const stepParcels = step.parcels?.length
    ? step.parcels
    : step.parcel
      ? [step.parcel]
      : [];
  const seen = new Set<string>();
  const references: ParcelReference[] = [];
  for (const stepParcel of stepParcels) {
    const parcelId = stepParcel?._id?.toString();
    if (!parcelId || seen.has(parcelId.toLowerCase())) {
      continue;
    }
    seen.add(parcelId.toLowerCase());
    references.push({
      stepId: step.id,
      stepName: step.name,
      stepType: StepType.OMS_ACTION,
      parcelId,
      parcelName: stepParcel?.name,
    });
  }
  return references;
}

/**
 * Steps that reference any of `parcelIds`. Code steps hardcode ids as string
 * literals, so they are matched textually and need their artifact source.
 */
export function findParcelReferences({
  steps,
  parcelIds,
  artifactCodeByArtifactId,
}: {
  steps: readonly ScannableStep[];
  parcelIds: Iterable<string>;
  artifactCodeByArtifactId?: ReadonlyMap<string, string>;
}): ParcelReference[] {
  const wanted = new Set<string>();
  for (const id of parcelIds) {
    wanted.add(id.toLowerCase());
  }

  const references: ParcelReference[] = [];

  for (const step of steps) {
    if (isSetParcelStep(step)) {
      references.push(
        ...actionStepReferences(step).filter((reference) =>
          wanted.has(reference.parcelId.toLowerCase()),
        ),
      );
      continue;
    }

    if (step.type === StepType.OMS_CODE && artifactCodeByArtifactId) {
      const code = step.artifactId
        ? artifactCodeByArtifactId.get(step.artifactId)
        : undefined;
      if (!code) {
        continue;
      }
      const seen = new Set<string>();
      for (const match of code.matchAll(OBJECT_ID_LITERAL)) {
        const normalized = match[0].toLowerCase();
        if (!wanted.has(normalized) || seen.has(normalized)) {
          continue;
        }
        seen.add(normalized);
        references.push({
          stepId: step.id,
          stepName: step.name,
          stepType: StepType.OMS_CODE,
          parcelId: match[0],
        });
      }
    }
  }

  return references;
}

/**
 * SET_PARCEL steps whose parcel no longer exists. Code steps are out of scope:
 * an unknown id literal is as likely to be a non-parcel id as a deleted parcel.
 */
export function findStaleParcelReferences({
  steps,
  liveParcelIds,
}: {
  steps: readonly ScannableStep[];
  liveParcelIds: Iterable<string>;
}): ParcelReference[] {
  const live = new Set<string>();
  for (const id of liveParcelIds) {
    live.add(id.toLowerCase());
  }
  return steps
    .filter(isSetParcelStep)
    .flatMap(actionStepReferences)
    .filter((reference) => !live.has(reference.parcelId.toLowerCase()));
}
